import React, { useEffect, useState } from "react";
import { ArrowLeft, LogOut } from "lucide-react";
import { getAllPlanForUnAuth } from "../api/apiFunction/planServices";
import { useNavigate } from "react-router-dom";
import {
  activateTrial,
  getTrialDetails,
} from "../api/apiFunction/userServices";
import { toast } from "react-toastify";
import { LogoWithText } from "../components/Logo";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const ActivateTrial = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Load logged-in user from localStorage for display + logout
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (error) {}
    }

    const fetchPlans = async () => {
      try {
        const res = await getAllPlanForUnAuth();
        if (res?.data) {
          setPlans(res.data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  useEffect(() => {
    let isMounted = true;
    const checkTrialAndRedirect = async () => {
      const localUser = localStorage.getItem("user")
        ? JSON.parse(localStorage.getItem("user"))
        : null;
      if (!localUser) return;

      // Prevent redirect if already on activate-trial page and trial is pending
      if (
        localUser.role === "individual" &&
        localUser?.trial?.status === "pending"
      ) {
        return;
      }

      try {
        const res = await getTrialDetails();
        if (!isMounted) return;

        const serverUser = res?.data?.user || null;
        const trial = serverUser?.trial || localUser?.trial || null;

        // Only update localStorage if trial data actually changed
        const trialChanged =
          JSON.stringify(localUser.trial) !== JSON.stringify(trial);
        const mergedUser = { ...localUser, trial };

        if (trialChanged) {
          localStorage.setItem("user", JSON.stringify(mergedUser));
        }

        if (mergedUser.role === "individual") {
          const status = trial?.status;
          // Only redirect if status changed from pending and not already on chat
          if (
            (status === "progress" || status === "paid") &&
            localUser?.trial?.status !== status &&
            window.location.pathname !== "/chat"
          ) {
            navigate("/chat", { replace: true });
          }
          return;
        }
        if (mergedUser.role === "admin") {
          if (window.location.pathname !== "/org/dashboard") {
            navigate("/org/dashboard", { replace: true });
          }
          return;
        }
        if (mergedUser.role === "employee" && mergedUser.plan) {
          if (window.location.pathname !== "/chat") {
            navigate("/chat", { replace: true });
          }
          return;
        }
      } catch {
        if (!isMounted) return;
        if (localUser.role === "individual") {
          const status = localUser?.trial?.status;
          // Only redirect if not already on the correct page
          if (
            (status === "progress" || status === "paid") &&
            window.location.pathname !== "/chat"
          ) {
            navigate("/chat", { replace: true });
          }
        }
      }
    };
    checkTrialAndRedirect();

    return () => {
      isMounted = false;
    };
  }, []); // Remove navigate from dependencies - it's stable and doesn't need to trigger re-runs

  const userObj = localStorage.getItem("user")
    ? JSON.parse(localStorage.getItem("user"))
    : null;
  const userRole = userObj?.role || "individual";
  const planType = userRole === "employee" ? "organization" : "individual";
  const filteredPlans = plans.filter((plan) => plan.planType === planType);
  const plan = filteredPlans[0];

  const formatLimit = (value) => {
    if (value === -1 || value === "Unlimited") return "Unlimited";
    return typeof value === "number" ? value.toLocaleString() : value || "N/A";
  };

  // For Activate Trial view, show Low-end tokens DAILY as (monthly / 30)
  // to avoid displaying "Unlimited" when monthly limit is finite.
  const getLowEndDailyFromMonthly = (planObj) => {
    const monthlySmall = planObj?.monthlyLimits?.smallLlmTokens;
    if (typeof monthlySmall === "number" && monthlySmall > 0) {
      const perDay = Math.floor(monthlySmall / 30);
      return perDay.toLocaleString();
    }
    // Fallback to existing daily limit formatting
    return formatLimit(planObj?.dailyLimits?.smallLlmTokens);
  };

  const features = plan
    ? [
        {
          label: "High-end tokens",
          daily: formatLimit(plan.dailyLimits?.highEndLlmTokens),
          monthly: formatLimit(plan.monthlyLimits?.highEndLlmTokens),
        },
        {
          label: "Low-end tokens",
          daily: getLowEndDailyFromMonthly(plan),
          monthly: formatLimit(plan.monthlyLimits?.smallLlmTokens),
        },
        {
          label: "Image generations",
          daily: formatLimit(plan.dailyLimits?.images),
          monthly: formatLimit(plan.monthlyLimits?.images),
        },
        {
          label: "Web searches",
          daily: formatLimit(plan.dailyLimits?.webSearches),
          monthly: formatLimit(plan.monthlyLimits?.webSearches),
        },
        {
          label: "YouTube",
          daily: formatLimit(plan.dailyLimits?.youtube),
          monthly: formatLimit(plan.monthlyLimits?.youtube),
        },
      ]
    : [];

  const handleStartTrial = async () => {
    const localUser = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : null;

    if (!localUser) {
      toast.error("Please sign in to start your free trial.");
      navigate("/login");
      return;
    }

    if (!plan) {
      toast.error("Plan not found.");
      return;
    }

    try {
      setIsPageLoading(true);

      // Call backend - it will create Stripe Checkout Session
      const res = await activateTrial();

      if (res?.data?.requiresCheckout && res?.data?.checkoutUrl) {
        // Store session ID for verification
        if (res.data.checkoutSessionId) {
          localStorage.setItem("trial_session_id", res.data.checkoutSessionId);
        }
        // Redirect to Stripe Checkout - Stripe handles all card validation
        window.location.href = res.data.checkoutUrl;
      } else if (res?.data?.user) {
        // Trial already active
        toast.success("Trial already active!");
        navigate("/chat");
      } else {
        toast.error("Failed to initiate trial.");
        setIsPageLoading(false);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error || err?.message || "Failed to start trial.";
      toast.error(msg);
      setIsPageLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    setUser(null);
    navigate("/login", { replace: true });
  };

  const getInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : "U";
  };

  const handleBack = () => navigate("/", { replace: true });

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-bg-50 overflow-hidden">
      {/* Left Section - Plan Details & Start Trial Button */}
      <div className="w-full lg:w-1/2 bg-bg-70 flex flex-col p-6 md:p-12 overflow-y-auto h-full">
        <div className="max-w-md mx-auto w-full">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 mb-16">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBack}
                className="p-2 hover:bg-bg-60 rounded-lg transition-colors"
                aria-label="Go back"
              >
                <ArrowLeft className="w-5 h-5 text-fg-60" />
              </button>
              <LogoWithText
                className="h-7 w-auto max-w-32"
                fillClassName="fill-fg-40 hover:fill-fg-30 transition-colors"
              />
            </div>

            {/* Logged-in user quick controls (avatar + logout) */}
            {user && (
              <div className="hidden md:flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-ac-02 flex items-center justify-center border border-bd-50">
                  <span className="text-white dark:text-black text-sm font-semibold">
                    {getInitial(user.name)}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-1.5 rounded-full text-fg-60 hover:bg-bg-60 transition-colors border border-bd-50 bg-bg-70"
                  title="Logout"
                  aria-label="Logout"
                >
                  <LogOut size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Plan Content */}
          {loading ? (
            <div className="space-y-6 animate-pulse">
              <div className="h-6 w-24 bg-bg-60 rounded-full"></div>
              <div className="h-8 w-48 bg-bg-60 rounded"></div>
              <div className="h-10 w-40 bg-bg-60 rounded"></div>
              <div className="pt-4">
                <div className="h-4 w-32 bg-bg-60 rounded mb-6"></div>
                <div className="space-y-3">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="grid grid-cols-3 gap-4">
                      <div className="h-4 w-32 bg-bg-60 rounded"></div>
                      <div className="h-4 w-16 bg-bg-60 rounded"></div>
                      <div className="h-4 w-20 bg-bg-60 rounded"></div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="h-10 w-full bg-bg-60 rounded mt-8"></div>
            </div>
          ) : plan ? (
            <>
              {/* User details card */}
              {user && (
                <div className="mb-8 p-4 rounded-2xl bg-bg-60 border border-bd-50 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-ac-02 flex items-center justify-center border border-bd-50">
                        <span className="text-white dark:text-black text-sm font-semibold">
                          {getInitial(user.name)}
                        </span>
                      </div>
                      <div className="flex flex-col leading-tight">
                        <span className="text-xs font-medium text-fg-40">
                          {user.name || "User"}
                        </span>
                        <span className="text-[11px] text-fg-60 truncate max-w-[220px]">
                          {user.email}
                        </span>
                      </div>
                    </div>
                    <span className="px-2 py-0.5 rounded-full bg-bg-50 text-[11px] text-fg-60 border border-bd-50">
                      {user.role || "individual"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-[11px] text-fg-60">
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-fg-70">
                        Plan
                      </span>
                      <span className="font-medium text-fg-40">
                        {user.plan?.name || "Pro trial"}
                      </span>
                    </div>
                    <div>
                      <span className="block text-[10px] uppercase tracking-wide text-fg-70">
                        Trial status
                      </span>
                      <span className="font-medium text-fg-40 capitalize">
                        {user.trial?.status || "pending"}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Trial Badge */}
              <div className="mb-6">
                <span className="inline-block px-3 py-1 bg-green-500/20 text-green-600 text-xs font-medium rounded-full border border-green-500/30">
                  7-Day Free Trial
                </span>
              </div>

              {/* Pricing */}
              <div className="mb-12">
                <div className="mb-4">
                  <span className="text-fg-60 text-sm">Start at</span>
                </div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-4xl font-bold text-fg-40">$0</span>
                  <span className="text-fg-60 text-lg">for 7 days</span>
                </div>
                <p className="text-sm text-fg-60">
                  Then ${plan.baseCost || 7}/month. Cancel anytime.
                </p>
              </div>

              {/* Usage Limits Table */}
              <div className="ml-1 mb-12">
                <h4 className="text-fg-60 text-sm font-medium mb-8">
                  What's included
                </h4>
                <div className="space-y-4 mb-4">
                  <div className="grid grid-cols-3 gap-4 pb-4 border-b border-bd-50">
                    <div className="text-fg-60 text-xs font-medium">
                      FEATURE
                    </div>
                    <div className="text-fg-60 text-xs font-medium">DAILY</div>
                    <div className="text-fg-60 text-xs font-medium">
                      MONTHLY
                    </div>
                  </div>
                  <div className="space-y-3">
                    {features.map((feature, index) => (
                      <div
                        key={`limit-${index}`}
                        className="grid grid-cols-3 gap-4"
                      >
                        <div className="text-fg-60 text-sm">
                          {feature.label}
                        </div>
                        <div className="text-fg-40 text-sm font-normal">
                          {feature.daily}
                        </div>
                        <div className="text-fg-40 text-sm font-normal">
                          {feature.monthly}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-fg-50 mb-4">
                No Plans Available
              </h3>
              <p className="text-fg-60 text-sm">Please check back later.</p>
            </div>
          )}

          {/* Bottom Section - Start Trial Button */}
          <div className="mb-4">
            {loading ? (
              <div className="space-y-3 animate-pulse">
                <div className="h-10 w-full bg-bg-60 rounded-lg"></div>
                <div className="h-4 w-full bg-bg-60 rounded"></div>
              </div>
            ) : (
              <>
                <button
                  onClick={handleStartTrial}
                  disabled={isPageLoading || !plan}
                  className="w-full bg-ac-02 text-white dark:text-black py-3 px-6 rounded-lg text-sm font-medium hover:bg-ac-01 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {isPageLoading
                    ? "Processing..."
                    : "Start Free Trial (7 days)"}
                </button>
                <p className="text-xs text-fg-60 text-center">
                  Your payment is secure and encrypted.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Right Section - Auth Showcase */}
      <AuthShowcase />

      {/* Full Page Loading Overlay */}
      {isPageLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-ac-02/30 border-t-ac-02 rounded-full animate-spin"></div>
            <p className="text-white text-sm font-medium">
              Redirecting to secure checkout...
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivateTrial;
